/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { connect } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useTranslation } from "react-i18next";
import { Field, reduxForm, formValueSelector } from "redux-form";

// Settings page components
import FormField from "components/FormField";
import FormSelector from "components/FormSelector";
import { useEffect, useState } from "react";
import UploadImageInput from "components/UploadImageInput/UploadImageInput";
import { newAlertMessage } from "store/actions/globalAlertActions";
import { uploadProjectImage, deleteProjectImage } from "store/actions/mainUserActions";

export const COUNTRIES = [
  { code: "ES", name: "Spain" },
  { code: "US", name: "United States" },
  { code: "MX", name: "Mexico" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "GB", name: "United Kingdom" },
  { code: "IT", name: "Italy" },
  { code: "MA", name: "Morocco" },
];

export const TIMEZONES_BY_COUNTRY = {
  ES: ["Europe/Madrid", "Atlantic/Canary"],
  US: [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Anchorage",
    "Pacific/Honolulu",
  ],
  MX: [
    "America/Mexico_City",
    "America/Cancun",
    "America/Tijuana",
    "America/Chihuahua",
    "America/Hermosillo",
  ],
  FR: ["Europe/Paris"],
  DE: ["Europe/Berlin"],
  GB: ["Europe/London"],
  IT: ["Europe/Rome"],
  MA: ["Africa/Casablanca"],
};

const validate = (values) => {
  const errors = {};
  const requiredFields = [
    "firstName",
    "lastName",
    "email",
    "address",
    "city",
    "postcode",
    "language",
    "phoneNumber",
  ];
  requiredFields.forEach((field) => {
    if (!values[field]) {
      errors[field] = "Required";
    }
  });

  if (values.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }

  if (!values.country) errors.country = "Required";

  if (values.country && !values.timeZone) errors.timeZone = "Required";

  return errors;
};

function ProjectForm(props) {
  const {
    handleSubmit,
    pristine,
    onSubmit,
    actionType,
    countryValue,
    timeZoneValue,
    change,
    projectId,
    mainUserUid,
    existingImageUid,
    formImageUid,
    actNewAlertMessage,
    actUploadProjectImage,
    actDeleteProjectImage,
  } = props;
  const { t } = useTranslation();
  const [imageData, setImageData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImageMarkedForDelete, setIsImageMarkedForDelete] = useState(false);

  const countryValues = COUNTRIES.map((c) => c.code);
  const countryNames = COUNTRIES.map((c) => ({ uid: c.code, name: c.name }));

  const availableTimeZones = countryValue ? TIMEZONES_BY_COUNTRY[countryValue] || [] : [];
  const timeZoneValues = availableTimeZones;
  const timeZoneNames = availableTimeZones.map((tz) => ({ uid: tz, name: tz }));

  useEffect(() => {
    if (!countryValue) {
      if (timeZoneValue) change("timeZone", null);
      return;
    }
    if (timeZoneValue && !availableTimeZones.includes(timeZoneValue)) {
      change("timeZone", null);
    }
  }, [countryValue]);

  const handleProjectSubmit = (formData) => {
    setIsSaving(true);

    const { name, address, city, postcode, country, timeZone } = formData;
    const projectData = {
      ...formData,
      name: name.trim(),
      address: address.trim(),
      city: city.trim(),
      postcode: postcode.trim(),
      country,
      timeZone,
    };

    if (!projectId) {
      actNewAlertMessage({
        type: "warning",
        title: "image",
        content: "missingProjectId",
      });
      onSubmit({
        ...projectData,
        imageUid: null,
      });
      setIsSaving(false);
      return;
    }

    if (!imageData) {
      if (isImageMarkedForDelete && existingImageUid && projectId) {
        actDeleteProjectImage({
          imageUid: existingImageUid,
          projectId,
          cb: () => {
            onSubmit({
              ...projectData,
              imageUid: null,
            });
            setIsSaving(false);
            setIsImageMarkedForDelete(false);
          },
        });
        return;
      }

      const uidToPersist = formData?.imageUid || existingImageUid || null;
      onSubmit({
        ...projectData,
        imageUid: uidToPersist,
      });
      setIsSaving(false);
      return;
    }

    actUploadProjectImage({
      image: imageData,
      projectId,
      cb: (imageUid) => {
        if (imageUid) {
          change("imageUid", imageUid);
          setImageData(null);
          actNewAlertMessage({
            type: "success",
            title: "image",
            content: `${t("uploadFile")} ${t("Success")}`,
          });
          onSubmit({
            ...projectData,
            imageUid,
          });
        } else {
          actNewAlertMessage({
            type: "error",
            title: "image",
            content: `${t("uploadFile")} ${t("unknownError")}`,
          });
        }
        setIsSaving(false);
      },
    });
  };

  const canSave =
    !isSaving &&
    (!pristine ||
      Boolean(imageData) ||
      (Boolean(formImageUid) && formImageUid !== existingImageUid));

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(handleProjectSubmit)}>
        <Grid container justifyContent="center" alignItems="center" sx={{ overflow: "visible" }}>
          <Card id="basic-info" sx={{ overflow: "visible" }}>
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("project")}
            </MDBox>

            <MDBox pt={3} pb={3} px={3}>
              <Grid container spacing={3}>
                <Field name="imageUid" component="input" type="hidden" />

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="name" component={FormField} label={t("name")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="address" component={FormField} label={t("Address")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="city" component={FormField} label={t("City")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field name="postcode" component={FormField} label={t("Postcode")} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="country"
                    component={FormSelector}
                    label={t("Country")}
                    values={countryValues}
                    names={countryNames}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="timeZone"
                    component={FormSelector}
                    label={t("Time zone")}
                    values={timeZoneValues}
                    names={timeZoneNames}
                    disabled={!countryValue}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                  <UploadImageInput
                    isProjectImg
                    label={t("image")}
                    helpText={`${t("onlyPng")} · 256x256`}
                    inputId="project-img-file-input"
                    selectLabel={t("Select")}
                    deleteLabel={t("Delete")}
                    uploadPlaceholder={t("uploadFile")}
                    projectId={projectId}
                    mainUserUid={mainUserUid}
                    existingImageUid={existingImageUid}
                    formImageUid={formImageUid}
                    onNewAlertMessage={actNewAlertMessage}
                    onSetImageData={(dataUrl) => {
                      setImageData(dataUrl);
                      if (dataUrl) setIsImageMarkedForDelete(false);
                    }}
                    onDeleteImage={(isDeleted) => setIsImageMarkedForDelete(isDeleted)}
                    onChangeImageUid={(uid) => change("imageUid", uid)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <MDBox display="flex" justifyContent="center" mt={2} xs={12}>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="dark"
                      size="small"
                      disabled={!canSave}
                    >
                      {isSaving ? t("Saving") : t("Save")}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

ProjectForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  change: PropTypes.func.isRequired,
  countryValue: PropTypes.string,
  timeZoneValue: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  mainUserUid: PropTypes.string.isRequired,
  existingImageUid: PropTypes.string,
  formImageUid: PropTypes.string,
  actNewAlertMessage: PropTypes.func.isRequired,
  actUploadProjectImage: PropTypes.func.isRequired,
  actDeleteProjectImage: PropTypes.func.isRequired,
};

ProjectForm.defaultProps = {
  countryValue: null,
  timeZoneValue: null,
  existingImageUid: null,
  formImageUid: null,
};

const formSelector = formValueSelector("ProjectForm");

const mapStateToProps = (state, props) => {
  const { actionType, projectId } = props;

  let projectDataToEdit = {};
  if (actionType === "edit" && projectId) {
    const { projectsReducer } = state;
    if (Object.prototype.hasOwnProperty.call(projectsReducer, projectId))
      projectDataToEdit = projectsReducer[projectId];
  }

  return {
    initialValues: projectDataToEdit,
    countryValue: formSelector(state, "country"),
    timeZoneValue: formSelector(state, "timeZone"),
    projectId,
    mainUserUid: state?.mainUserReducer?.uid || "",
    existingImageUid: projectDataToEdit?.imageUid || "",
    formImageUid: formSelector(state, "imageUid") || "",
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
  actUploadProjectImage: (data) => dispatch(uploadProjectImage(data)),
  actDeleteProjectImage: (data) => dispatch(deleteProjectImage(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "ProjectForm", // a unique identifier for this form
    validate,
  })(ProjectForm)
);
